import {
  BLUR_LEVELS,
  DIRECTION_DICTIONARY,
  QUALITY_THRESHOLD,
} from "./constants";
import { setup } from "./engine";
import {
  addInfo,
  getArrAdjacent,
  randomArr,
  randomArrAdjacent,
  randomNumBetween,
  scoreWeight,
  seedToName,
  unique,
} from "./utils";
import { Vector } from "./vector";

export const run = async (
  componentsLoader,
  options = {},
  retryProtection = 0,
  callback = () => {}
) => {
  const { getComponents, getOptions } = await setup(componentsLoader, options);

  const {
    dotsPerRow,
    dotsPerColumn,
    width,
    height,
    dotWidth,
    dotHeight,
    seed,
    bounds,
    browser,
    padding,
    autoskip,
    debug,
  } = getOptions();
  const { ctx, noise, random } = getComponents();

  ctx.fillStyle = "white";
  ctx.strokeStyle = "white";

  // actual X/Y
  const ax = (x, middled = true) => dotWidth * x + (middled ? dotWidth / 2 : 0);
  const ay = (y, middled = true) =>
    dotHeight * y + (middled ? dotHeight / 2 : 0);

  // Method to knock points off their grid axis
  const offset = (val, x, y, mod = dotWidth) => {
    const offsetVal = noise.noise2D(x, y);

    return val + offsetVal * mod;
  };

  if (browser) {
    document.querySelector(".seed").innerHTML = seed;
    window.SEED = seed;
    window.history.pushState(
      {},
      null,
      `${window.location.pathname}?seed=${SEED}`
    );
  }

  /**
   * Utility to just visualise the dots
   */
  const drawDots = () => {
    ctx.save();
    ctx.fillStyle = "#333";
    for (let x = 0; x < dotsPerRow; x++) {
      for (let y = 0; y < dotsPerColumn; y++) {
        ctx.drawDot(ax(x), ay(y));
        ctx.fill();
      }
    }
    ctx.restore();
  };

  /**
   *
   * @returns {Vector}
   */
  const getRandomSpot = () => {
    return new Vector(
      Math.floor(randomNumBetween(dotsPerRow, 0)),
      Math.floor(randomNumBetween(dotsPerColumn, 0))
    );
  };

  const getRandomSpotAway = (prevSpot, minDistance = 2) => {
    let spot = getRandomSpot();

    while (spot.distance(prevSpot) < minDistance) {
      spot = getRandomSpot();
    }

    return spot;
  };

  /**
   *
   * @returns {{
   *  points: Vector[]
   * }}
   */
  const createThread = () => {
    let lastDir = randomArr(DIRECTION_DICTIONARY);
    const points = [getRandomSpot()];
    const sizes = [random()];
    const blurs = [randomArr(BLUR_LEVELS)];
    const totalPoints = randomNumBetween(22, 15);
    // const totalPoints = 3;

    let target = getRandomSpotAway(points[0]);

    const _devTargets = [target];

    let killswitch = 0;

    while (points.length < totalPoints && killswitch < 50) {
      const nextDirs = getArrAdjacent(lastDir, DIRECTION_DICTIONARY, 2);

      const distances = nextDirs.map((dir) => {
        return {
          dir,
          distance: points[points.length - 1].clone().add(dir).distance(target),
        };
      });

      const targetHit = distances.every(({ distance }) => distance < 4);

      if (targetHit) {
        target = getRandomSpotAway(target);
        _devTargets.push(target);
      }

      distances.sort((a, b) => {
        return a.distance - b.distance;
      });

      const nextDir = distances[0].dir;

      const nextPoint = points[points.length - 1].clone().add(nextDir);

      if (bounds.check(nextPoint)) {
        points.push(nextPoint);
        sizes.push(random());
        blurs.push(randomArr(BLUR_LEVELS));
      }

      lastDir = nextDir;
      killswitch++;
    }

    if (killswitch >= 50) {
      console.log("killswitch tripped");
    }

    // const points = [new Vector(5, 6), new Vector(6, 6), new Vector(6, 5)];
    // const sizes = [0.5, 0.5, 0.5];
    // const blurs = [BLUR_LEVELS[0], BLUR_LEVELS[0], BLUR_LEVELS[0]];
    // const _devTargets = [];

    return {
      points,
      sizes,
      blurs,
      _devTargets,
      evaluate: () => {
        const spreadScore = () => {
          const tl = new Vector(0, 0);
          const tr = new Vector(dotsPerColumn - 1, 0);
          const bl = new Vector(0, dotsPerRow - 1);
          const br = new Vector(dotsPerColumn - 1, dotsPerRow - 1);

          const corners = [tl, tr, bl, br];

          const distances = corners.map((point) => {
            return Math.min(...points.map((p) => point.distance(p)));
          });

          const maxDistance = tl.distance(br);

          return (
            1 -
            distances.reduce((p, c) => {
              if (c > maxDistance / 0.25) {
                return p + 0;
              }

              return p + c / maxDistance;
            }, 0) /
              distances.length
          );
        };

        const angleScore = () => {
          const angles = points
            .map((p, idx, arr) => {
              if (idx > 0) {
                // return p.angle(arr[idx - 1]);
                return arr[idx - 1].angle(p);
              }

              return false;
            })
            .slice(1)
            .map((a, idx, arr) => {
              if (idx > 0) {
                return Math.abs(a - arr[idx - 1]);
              }
            })
            .slice(1);

          return 1 - angles.reduce((p, c) => p + c, 0) / angles.length / 360;
        };

        return scoreWeight(
          [
            { score: spreadScore(), weight: 0.8, key: "spread" },
            { score: angleScore(), weight: 0.2, key: "angles" },
          ],
          browser
        );
      },
      getActualPoint: (idx) => {
        return new Vector(
          offset(ax(points[idx].x), points[idx].x, points[idx].y, dotWidth),
          offset(ay(points[idx].y), points[idx].x, points[idx].y, dotHeight)
        );
      },
      name: () => {
        return seedToName(seed);
      },
    };
  };

  const drawThread = (thread) => {
    unique(BLUR_LEVELS).forEach((currentBlurLevel, idx) => {
      ctx.save();
      ctx.filter = `blur(${currentBlurLevel / (browser ? 1 : 2)}px)`;

      thread.points.forEach((point, idx, arr) => {
        if (idx !== 0 && thread.blurs[idx] === currentBlurLevel) {
          const { x: lastX, y: lastY } = thread.getActualPoint(idx - 1);
          const { x, y } = thread.getActualPoint(idx);

          const middlePoint = new Vector(lastX, lastY).lerp(
            {
              x,
              y,
            },
            0.5
          );

          const middlePoint1 = middlePoint.getPointOnRadius(
            90,
            thread.sizes[idx] * 3
          );
          const middlePoint2 = middlePoint.getPointOnRadius(
            270,
            thread.sizes[idx] * 3
          );

          ctx.beginPath();
          ctx.moveTo(lastX, lastY);
          ctx.quadraticCurveTo(middlePoint1.x, middlePoint1.y, x, y);
          ctx.quadraticCurveTo(middlePoint2.x, middlePoint2.y, lastX, lastY);
          ctx.closePath();
          ctx.fill();
          // ctx.stroke();
        }
      });

      thread.points.forEach((point, idx) => {
        if (thread.blurs[idx] === currentBlurLevel) {
          const { x, y } = thread.getActualPoint(idx);
          ctx.drawDot(x, y, thread.sizes[idx] * 5);
          ctx.fill();
        }
      });
      ctx.restore();
    });

    if (debug) {
      ctx.fillStyle = "red";
      thread._devTargets.forEach((point, idx) => {
        ctx.drawDot(ax(point.x), ay(point.y), idx + 3);
        ctx.fill();
      });
    }
  };

  if (debug) {
    drawDots();
  }
  const thread = createThread();

  addInfo("Value", `${Math.round(thread.evaluate() * 100)}%`, browser);

  if (browser) {
    addInfo("Run", `yarn server --seed ${seed}`, browser);
  }

  addInfo("Name", thread.name(), browser);

  if (
    thread.evaluate() < QUALITY_THRESHOLD &&
    retryProtection < 100 &&
    autoskip
  ) {
    return run(componentsLoader, options, retryProtection + 1);
  } else {
    if (retryProtection >= 10) {
      console.log(`Retry protection tripped`);
    }
    drawThread(thread);
    return { getComponents, getOptions, thread };
  }
};
